/**
 * Utility for creating color ramps based on sequential color scales.
 * Provides linear interpolation between color stops over a numeric domain.
 */

/** RGB color object for parsed values */
interface RgbColor {
  r: number;
  g: number;
  b: number;
}

/** Plotly’s Yellow-Orange-Red 9-color scale (YlOrRd9) */
const yellowOrangeRed9: readonly string[] = [
  "rgb(255,255,204)",
  "rgb(255,237,160)",
  "rgb(254,217,118)",
  "rgb(254,178,76)",
  "rgb(253,141,60)",
  "rgb(252,78,42)",
  "rgb(189,0,38)",
  "rgb(128,0,38)"
];

/**
 * Parses an RGB string (e.g., "rgb(255,0,0)") into an RgbColor object.
 * @param rgbString - The RGB string to parse.
 * @returns The parsed RGB color object.
 * @throws Error if the string is not a valid RGB format.
 */
function parseRgbString(rgbString: string): RgbColor {
  const match = rgbString.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!match) {
    throw new Error(`Invalid RGB string: ${rgbString}`);
  }
  return {
    r: Number(match[1]),
    g: Number(match[2]),
    b: Number(match[3])
  };
}

/**
 * Creates a color ramp function that interpolates between color stops over a domain.
 * @param colorStops - Array of RGB strings defining the color scale.
 * @param domain - The [min, max] range for interpolation.
 * @returns A function that maps a number to an interpolated RGB string.
 */
function createColorRamp(
  colorStops: readonly string[],
  domain: [number, number]
) {
  const [domainMin, domainMax] = domain;
  const stopsCount = colorStops.length;

  return (value: number): string => {
    // Clamp value to domain
    const normalizedValue = Math.min(
      1,
      Math.max(0, (value - domainMin) / (domainMax - domainMin))
    );
    const position = normalizedValue * (stopsCount - 1);
    const index = Math.floor(position);
    const fraction = position - index;

    // Handle edge case for last stop
    if (index >= stopsCount - 1) {
      return colorStops[stopsCount - 1];
    }

    // Interpolate between stops
    const color1 = parseRgbString(colorStops[index]);
    const color2 = parseRgbString(colorStops[index + 1]);
    const r = Math.round(color1.r + (color2.r - color1.r) * fraction);
    const g = Math.round(color1.g + (color2.g - color1.g) * fraction);
    const b = Math.round(color1.b + (color2.b - color1.b) * fraction);

    return `rgb(${r},${g},${b})`;
  };
}

/** Pre-configured color ramp for weeks (0-25) using Yellow-Orange-Red scale */
export const yellowOrangeRedPalette = createColorRamp(
  yellowOrangeRed9,
  [0, 25]
);
